import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import {
  STAR_RATING_RANGE,
  parseCumulativeProductCatalogCountRatingResponse,
  parseWeeklyProductCatalogCountRatingData,
  useLatestCumulativeReviewWeekId
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' },
  { label: 'Product Catalog Count by Weekly', id: 'weeklyProductCatalogCount' }
];

export const useLifetimeProductCatalogCountReviewRatingData = () => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);

  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const requestArray = STAR_RATING_RANGE.map(({ range: [min, max] }) => {
    const aggregationByLifeTimeReviewRating = new AggregationBuilder('retailerId')
      .addAggregationField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .setSortDirection(null)
      .setGroupByFieldName('retailerId')
      .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
      .addComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek)
      .build();

    const requestByLifetimeReviewRating = new AdvancedSearchRequestBuilder(
      `mainEntityMetrics-client-${beaconClientId}-productCatalogCountReviewRatingData`,
      'beacon-ratings-reviews-metrics-v2'
    )
      .setPageNumber(1)
      .setPageSize(10000)
      .setReturnAdditionalMetadata(false)
      .setRetailerId(retailer.id)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('retailerId', 'should', [retailer.id])
      .setSearchBy('parent')
      .addAggregation(aggregationByLifeTimeReviewRating)
      .addSortField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    requestByLifetimeReviewRating.addConditionRangeFilter('lifetimeReviewAverageRating', min, max);
    return requestByLifetimeReviewRating.build();
  });

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'productCatalogCountReviewRating-columnChart',
    requestBody: requestArray,
    queryKeys: requestArray
  });

  return { isLoading, data };
};

export const useWeeklyProductCatalogCountReviewRatingData = ({
  shouldStartFetch = false
}: {
  shouldStartFetch: boolean;
}) => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const aggregationByWeeklyReviewRating = new AggregationBuilder('stacklineSku')
    .addAggregationField('Weighted Weekly Review Rating', 'weightedWeeklyReviewRating', 'sum', false)
    .addAggregationField('Weekly Review Volume', 'weeklyReviewVolume', 'sum', false)
    .addAggregationField(
      'Weekly Review Rating',
      'weeklyReviewRating',
      'computed',
      false,
      '(weeklyReviewVolume_sum > 0 ? weightedWeeklyReviewRating_sum / weeklyReviewVolume_sum : 0)'
    )
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addConditionRangeFilter('weeklyReviewAverageRating', 1, 5)
    .build();

  const requestByWeeklyReviewRating = new AdvancedSearchRequestBuilder(
    `mainEntityMetrics-client-${beaconClientId}-productCatalogCountRatingData-weekly`,
    `beacon-${BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}`
  )
    .setPageNumber(1)
    .setPageSize(65000)
    .setRetailerId(retailer.id)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setSearchBy('parent')
    .addAggregation(aggregationByWeeklyReviewRating)
    .setReturnAdditionalMetadata(false)
    .addSortField('weeklyReviewRatingVolume', 'childWeeklyRatingVolume', 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const comparisonAggregationByWeeklyRating = new AggregationBuilder('stacklineSku')
    .addAggregationField('Weighted Weekly Rating', 'weightedWeeklyReviewRating', 'sum', false)
    .addAggregationField('Weekly Review Volume', 'weeklyReviewVolume', 'sum', false)
    .addAggregationField(
      'Weekly Review Rating',
      'weeklyReviewRating',
      'computed',
      false,
      '(weeklyReviewVolume_sum > 0 ? weightedWeeklyReviewRating_sum / weeklyReviewVolume_sum : 0)'
    )
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek)
    .addConditionRangeFilter('weeklyReviewRating', 1, 5)
    .build();

  const comparisonRequestByWeeklyReviewRating = requestByWeeklyReviewRating
    .clone()
    .clearAggregations()
    .addAggregation(comparisonAggregationByWeeklyRating)
    .clearConditionRangeFilters()
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
  const requestArray = [requestByWeeklyReviewRating.build(), comparisonRequestByWeeklyReviewRating.build()];

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'productCatalogCountWeeklyRating-columnChart',
    requestBody: requestArray,
    queryKeys: requestArray,
    shouldPerformFetch: shouldStartFetch
  });

  return { isLoading, data };
};

/**
 *
 *  Product Catalog Count by (Cumulative/Weekly) Review Rating Tier in Review (Analysis) page in Review tab.
 */

const ProductCatalogCountReviewRatingChart = () => {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const [selectedMetric, setSelectedMetric] = useState<'cumulativeProductCatalogCount' | 'weeklyProductCatalogCount'>(
    'cumulativeProductCatalogCount'
  );
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { data: cumulativeData, isLoading: cumulativeLoading } = useLifetimeProductCatalogCountReviewRatingData();
  const { data: weeklyData, isLoading: weeklyLoading } = useWeeklyProductCatalogCountReviewRatingData({
    shouldStartFetch: selectedMetric === 'weeklyProductCatalogCount'
  });

  const { compareData, currentData } = cumulativeData
    ? parseCumulativeProductCatalogCountRatingResponse({
        data: cumulativeData,
        comparisonStartWeekToUse: comparisonStartWeek,
        comparisonEndWeek,
        selectedMetric
      })
    : { compareData: [], currentData: [] };

  const { compareData: weeklyCompareData, currentData: weeklyCurrentData } = weeklyData
    ? parseWeeklyProductCatalogCountRatingData({ data: weeklyData, field: 'review' })
    : { compareData: [], currentData: [] };

  return (
    <BeaconChartWithLegend
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(
          selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
          selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
          { metricLabel: 'Product Catalog Count' }
        );
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each review rating tier
            {selectedMetric === 'cumulativeProductCatalogCount'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: productCatalogCountOptions,
        selectedId: selectedMetric,
        onChange: (option) => setSelectedMetric(option.id),
        suffix: 'Review Rating Tier'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={cumulativeLoading || weeklyLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            color: '#adbdcc',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ProductCatalogCountReviewRatingChart;
